import { inject, Injectable } from '@angular/core';
import { CanMatchFn, Route, UrlSegment } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { SubPagesListApiResponse } from '@modules/whitelabel-cover-page/sub-pages/sub-pages.interface';
import { APIENDPOINT } from '@core/api-url.enum';
import { ApiCacheService, CacheTypes } from '@core/cache/api-cache.service';
import { extractWhiteLabelConfig, hasWhiteLabelConfig } from '@utils/common.utils';
import { SubPagesApiService } from '@modules/whitelabel-cover-page/sub-pages/sub-pages-api.service';
import {
  getGeneratedRootSubPage,
  getWhiteLabelRootOwnerUsername,
  isKnownWhiteLabelRootSubPage,
  isWhiteLabelRootSlugTreatedAsProfile,
  markWhiteLabelRootSlugAsProfile,
  markWhiteLabelRootSlugAsSubPage
} from '@modules/sub-page-view/sub-page-view.constants';
import {
  findSubPageByRoute,
  isPublishedSubPage
} from '@modules/sub-page-view/sub-page-view.utils';

@Injectable({
  providedIn: 'root'
})
export class WhiteLabelRootSlugResolverService {
  private readonly inflight = new Map<string, Observable<boolean>>();

  constructor(
    private readonly apiCacheService: ApiCacheService,
    private readonly subPagesApi: SubPagesApiService
  ) {}

  isRootSubPage(slug: string): Observable<boolean> {
    const normalized = (slug || '').trim().toLowerCase();
    if (!normalized) {
      return of(false);
    }
    if (isWhiteLabelRootSlugTreatedAsProfile(normalized)) {
      return of(false);
    }
    if (isKnownWhiteLabelRootSubPage(normalized)) {
      markWhiteLabelRootSlugAsSubPage(normalized);
      return of(true);
    }

    const pending = this.inflight.get(normalized);
    if (pending) {
      return pending;
    }

    const request$ = this.resolveUnknownSlug(normalized).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.inflight.set(normalized, request$);
    return request$;
  }

  private resolveUnknownSlug(slug: string): Observable<boolean> {
    return this.findLiveSubPage(slug).pipe(
      switchMap((isSubPage) => {
        if (isSubPage) {
          markWhiteLabelRootSlugAsSubPage(slug);
          return of(true);
        }
        return this.markProfileIfUser(slug);
      }),
      catchError(() => of(false)),
      finalize(() => this.inflight.delete(slug))
    );
  }

  private markProfileIfUser(slug: string): Observable<boolean> {
    return this.apiCacheService
      .get(APIENDPOINT.user(slug), CacheTypes.REFRESH_EVERY_N)
      .pipe(
        map((response: { status?: boolean; data?: unknown }) => {
          if (response?.status && response?.data) {
            markWhiteLabelRootSlugAsProfile(slug);
          }
          return false;
        }),
        catchError(() => of(false))
      );
  }

  private findLiveSubPage(slug: string): Observable<boolean> {
    const ownerUsername = getWhiteLabelRootOwnerUsername();
    const ownerId = this.getOwnerId();
    const ownerList$ = this.subPagesApi
      .getSubPages(undefined, CacheTypes.REFRESH_EVERY_N)
      .pipe(catchError(() => of(null)));
    const publicList$ = ownerId
      ? this.subPagesApi.getPublicSubPages(ownerId).pipe(catchError(() => of(null)))
      : of(null);

    return ownerList$.pipe(
      switchMap((ownerResponse) => {
        if (this.listHasSlug(ownerResponse, ownerUsername, slug)) {
          return of(true);
        }
        return publicList$.pipe(
          map((publicResponse) => this.listHasSlug(publicResponse, ownerUsername, slug))
        );
      })
    );
  }

  private listHasSlug(
    response: SubPagesListApiResponse | null,
    ownerUsername: string,
    slug: string
  ): boolean {
    return isPublishedSubPage(findSubPageByRoute(response?.data, ownerUsername, slug), true);
  }

  private getOwnerId(): string {
    const ownerId = extractWhiteLabelConfig()?.user?.gsi4pk;
    return typeof ownerId === 'string' ? ownerId.trim() : '';
  }
}

export const whiteLabelRootSubPageCanMatch: CanMatchFn = (
  _route: Route,
  segments: UrlSegment[]
): boolean | Observable<boolean> => {
  if (!hasWhiteLabelConfig() || segments.length !== 1) {
    return false;
  }

  const slug = segments[0]?.path?.trim() ?? '';
  if (!slug) {
    return false;
  }

  if (isWhiteLabelRootSlugTreatedAsProfile(slug)) {
    return false;
  }

  if (getGeneratedRootSubPage(slug) && isKnownWhiteLabelRootSubPage(slug)) {
    markWhiteLabelRootSlugAsSubPage(slug);
    return true;
  }

  return inject(WhiteLabelRootSlugResolverService).isRootSubPage(slug);
};
